import React, {useState, useEffect, useRef} from "react";
import {QueryClient, QueryClientProvider} from "react-query";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Tab,
    Divider,
    IconButton,
    Avatar,
    Link,
} from "@mui/material";
import {
    ACard,
    ADataGridFilter,
    PagedSearchParams
} from "@atiautomacao/ati-ui-library";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";
import {Ticket} from "../../../Shared/Types/Ticket";
import {SearchTickets} from "../../Settings/Ticket/TicketReactQuery";
import {TicketUpdateModal} from "./TicketModal";
import { useAppSelector } from "../../../Config/Hooks";
import { hasPermission } from "../../../Shared/Auth/AuthenticationUtil";
import { AUTHORITIES } from "../../../Config/Constants";
import { TabContext, TabList } from "@mui/lab";
import { ArrowForwardOutlined, CheckCircleOutlined, ErrorOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { DateFormat } from "../../../Utils/DateFormatPatternUtils";
import { useNavigate } from "react-router-dom";
import { SystemRoutes } from "../../../Utils/RouteUtils";
type TicketItemProps = {
    ticket: Ticket,
    handleSelect: Function
};

const TicketItem = ({ticket, handleSelect}: TicketItemProps) => {
    const theme = useTheme();
    const account = useAppSelector(state => state.authentication.account);
          const isAuthorizedToTicketSave = hasPermission(account?.authoritySet, [
              AUTHORITIES.SYSADMIN, 
              AUTHORITIES.SAVE_TICKET
          ]);
      
          const isAuthorizedToTicketSearch = hasPermission(account?.authoritySet, [
              AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET, AUTHORITIES.VIEW_TICKET
          ]);
      
          const isAuthorizedToTicketCategorySearch = hasPermission(account?.authoritySet, [
              AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET_CATEGORY, AUTHORITIES.VIEW_TICKET_CATEGORY
          ]);
      
          const isAuthorizedToTicketSubCategorySearch = hasPermission(account?.authoritySet, [
              AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET_SUBCATEGORY, AUTHORITIES.VIEW_TICKET_SUBCATEGORY
          ]);
      
          const isAuthorizedToView = () => {
      
              if(isAuthorizedToTicketSave && isAuthorizedToTicketSearch && isAuthorizedToTicketCategorySearch && isAuthorizedToTicketSubCategorySearch ){
                  return true;
              }
              return false;
          
          }
    const unknownTypeColor = '#929fb3';
    const iconColor = ticket.status === 'CLOSED'
        ? 'rgba(27, 180, 131, 1)'
        : ticket.type === 'DEFECT'
            ? 'red'
            : ticket.type === 'MAINTENANCE'
                ? 'rgba(255, 179, 0, 1)'
                : ticket.type === 'OTHER'
                    ? unknownTypeColor
                    : unknownTypeColor;
    
    return (
        <>
        <Card
            sx={{
                borderRadius: 0,
                maxWidth: '100%',
                color: '#000',
                border: 'none',
                boxShadow: 'none'
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'row', border: 'none' }}>                       
                    <Grid container spacing={'8px'} color={'text.secondary'}>
                        <Grid item xs={12} display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ bgcolor: iconColor, width: 32, height: 32 }}>

                                { ticket.status === 'CLOSED' ?
                                    <CheckCircleOutlined  fontSize="small"/>
                                    :
                                    <ErrorOutlined fontSize="small" />
                                }
                            </Avatar>
                            <Typography variant="h6" color={theme.palette.text.primary}
                             >Usina {ticket.powerStationName}</Typography>
                        </Grid>
                         <Grid item xs={12} lg={4} xl={4} sx={{display: "flex", alignItems: "center"}}>
                            <Typography variant="body1">
                            <b>Componente:</b> {ticket.skidName ? `| ${ticket.skidName}` : ''} {ticket.equipmentName ? `| ${ticket.equipmentName}` : ''}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} lg={4} xl={4} sx={{display: "flex", alignItems: "center"}}>
                            <Typography variant="body1">
                            <b>Assunto:</b> {ticket.subject}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}  lg={2} xl={2} sx={{display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: '-8px' }}>
                            <Typography variant="body1">
                                <p style={{ margin: 0 }}>
                                <b>Data:</b> {ticket.scheduling ? DateFormat(`${ticket.scheduling}T00:00:00`, 'dd/MM/yyyy') : ""} 
                                </p>
                            </Typography>

                        </Grid>
                        <Grid item xs={6}  lg={2} xl={2} sx={{display: "flex", alignItems: "center", justifyContent: "end", marginTop: '-8px' }}>
                            <IconButton size="small" color="primary" title="Mais Detalhes" 
                                onClick={() => handleSelect(ticket)} disabled={!isAuthorizedToView()}>
                                <ArrowForwardOutlined />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Box>
            </CardContent>
            <Divider />

        </Card>
        </>
    );
};

type TicketWidgetComponentProps = {
    searchParams?: {
        powerStationId?: string;
    };
};

const TicketWidgetComponent = ({ searchParams }: TicketWidgetComponentProps) => {
    const navigate = useNavigate();
    const listRef = useRef<HTMLDivElement>(null);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(new Array<ADataGridFilter>(), 0, 10));
    const {data} = SearchTickets(pagedSearchParams);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [ticketIdSelected, setTicketIdSelected] = useState<number | null>();
    const [openModal, setOpenModal] = useState(false);
    const [paramStatus, setParamStatus] = useState("OPENED");

    useEffect(() => {
        const filters: Array<ADataGridFilter> = new Array<ADataGridFilter>();

        // filter by status
        if (paramStatus !== 'ALL') {
            filters.push({
                name: 'status',
                condition: 'equals',
                value: paramStatus,
                sort: 'none'
            });
        }

        // filter by powerStationName
        if (searchParams?.powerStationId) {
            filters.push({
                name: 'powerStationId',
                condition: 'equals',
                value: searchParams.powerStationId,
                sort: 'none'
            });
        }

        // sort by id
        filters.push({
            name: 'id',
            condition: 'equals',
            value: '',
            sort: 'desc'
        });

        setPagedSearchParams(new PagedSearchParams(filters, 0, 10));
    }, [paramStatus, searchParams]);

    useEffect(() => {
        setTickets(data?.data);
    }, [data]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = 0; // Força o scroll para o inicio
        }
    }, [tickets]);

    const handleSelect = (ticket?: Ticket) => {
        if (ticket) {
            setTicketIdSelected(ticket.id);
            setOpenModal(true);
        }
    };

    const handleCloseModal = () => {
        setTicketIdSelected(null);
        setOpenModal(false);
    }

    const handleNavigation = () => {
        console.log("Navigating to all tickets page");
        navigate(SystemRoutes.SETTINGS_TICKET);
    }

    return (
        <>
        <ACard
            title="Últimos Tickets"
            styleProps={{
                cardStyle: { height: 500, padding:0, margin: 0},
                subTitleStyle: {display: 'none'},
                contentStyle: {paddingTop: 0, paddingBottom: 0},
                footerStyle: {padding: '24px' }
            }}
            
            footerActions={
               <Link href="#" onClick={(event) => handleNavigation()} underline="hover" style={{ display: 'flex', justifyContent: 'flex-end', 
               alignItems: 'center' }}>
                    <ArrowForwardOutlined  sx={{marginRight: '12px'}}/>
                    <Typography variant="caption">VER TODOS OS TICKETS</Typography>
                </Link>
            }
        >
             <Box sx={{ width: '100%', typography: 'body1' }}>
                    <TabContext value={paramStatus}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList onChange={(event, newValue) => setParamStatus(newValue)} aria-label="lab API tabs example">
                                <Tab label="Abertos" value="OPENED" />
                                <Tab label="Fechados" value="CLOSED" />
                                <Tab label="Todos" value="ALL" />
                            </TabList>
                        </Box>

                    </TabContext>
                </Box>
            {/* Área de tickets */}
            <Box
                ref={listRef}
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "10px",
                    minHeight: "298px",
                    maxHeight: "300px",
                }}
            >
                {tickets?.length > 0 ? tickets.map((ticket, index) => (
                    <>
                        <TicketItem ticket={ticket} handleSelect={handleSelect} />
                    </>
                )) :
                    <DataNotFound boxStyle={{ height: 289, width: '100%' }} />
                }
            </Box>
        </ACard>

        {/* Ticket Modal */}
        <TicketUpdateModal ticketId={ticketIdSelected} open={openModal} onClose={handleCloseModal} />
        </>
    );
};

export default function TicketWidget({ searchParams }: TicketWidgetComponentProps) {
    const queryClient = new QueryClient();

    return (
        <QueryClientProvider client={queryClient}>
            <TicketWidgetComponent searchParams={ searchParams } />
        </QueryClientProvider>
    )
}